import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "../../../configs/apiRoutes";
import instance from "../../../api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import { Translation } from "../types";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";
import type { RootState } from "store";

export interface TagItem {
  id: number;
  title: string;
  description: string;
  slug: string;
  isActive: string;
  image: string;
  countOfGames: number;
  icon?: string;
  translations: Array<Translation>;
}
export interface TagsBunchPayload {
  ids: number[];
  isActive: number;
}
interface TagPayload {
  id?: number;
  title?: string;
  description?: string;
  slug?: string;
  isActive?: boolean;
  image?: string | File;
  icon?: string | File;
  translations?: Array<Translation>;
}
interface TagState {
  list: TagItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  selectedItems: number[];
  isItemFetching: boolean;
  isItemLocaleFetching: boolean;
  payload?: TagPayload;
  initialPayload?: TagPayload;
  isItemFetched: boolean;
  processingItemId?: number;
}
interface GetListPayload {
  limit: number;
  offset: number;
  isActive?: number;
  title: string;
  order: {
    title: string;
    slug: string;
    countOfGames: string;
  };
}

interface AddGamesPayload {
  gameIds: number[];
  geos: string[];
  tag: number;
}
export const addGamesToTag = createAsyncThunk(
  "game-center/tags/add-games",
  async (data: AddGamesPayload) => {
    try {
      const result = await instance.put(
        `${apiRoutes.tagBase}/${data.tag}/add-games`,
        {
          gameIds: data.gameIds,
          geos: data.geos,
        },
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getTagsList = createAsyncThunk(
  "game-center/tags/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.tagsList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createTag = createAsyncThunk<
  number,
  TagPayload,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/tags/create",
  async (payload: TagPayload, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    data.append("title", (payload?.title as string) ?? "");
    data.append("slug", (payload.slug as string) ?? "");
    data.append("description", (payload.description as string) ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      const response = await instance.post(`${apiRoutes.tagsCreate}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateTag = createAsyncThunk<
  boolean,
  TagPayload,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/tags/update",
  async (payload: TagPayload, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    data.append("title", payload?.title as string);
    data.append("slug", payload.slug as string);
    data.append("description", payload.description as string);
    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      await instance.put(`${apiRoutes.tagsUpdate}/${payload.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateTagsBunch = createAsyncThunk(
  "game-center/tags/update-bunch",
  async (data: TagsBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updateTagsBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getTagItem = createAsyncThunk(
  "game-center/tags/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.tagsRead}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const deleteTagItem = createAsyncThunk(
  "game-center/tags/delete",
  async (id: string | number) => {
    try {
      const result = await instance.delete(`${apiRoutes.tagsDelete}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getTagItemLocale = createAsyncThunk(
  "game-center/tags/get-item-locale",
  async ({ id, locale }: { id: string | number; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.tagsRead}/${id}/translation/${locale}`,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updateTagItemLocale = createAsyncThunk(
  "game-center/tags/update-item-locale",
  async (
    { id, locale }: { id: string | number; locale: Partial<Translation> },
    { getState },
  ) => {
    try {
      const { locale: localeName, ...restData } = locale;
      const initialTranslation = (
        getState() as RootState
      ).tags.initialPayload?.translations?.find(
        (translation) => translation.locale === localeName,
      );
      const dataToUpdate = getChangedFields(
        initialTranslation,
        restData,
        ["title", "description", "isActive"],
        {
          title: normalizeOptionalText,
          description: normalizeSeoHtml,
          isActive: Boolean,
        },
      );

      await instance.put(
        `${apiRoutes.tagsRead}/${id}/translation/${localeName}`,
        dataToUpdate,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: TagState = {
  list: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetched: false,
  isItemFetching: false,
  isItemLocaleFetching: false,
};

export const tagsSlice = createSlice({
  name: "tags",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: TagState,
      action: PayloadAction<TagPayload>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: TagState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
    handleInvalidateList: (state: TagState) => {
      state.isListFetched = false;
    },
    handlePaginate: (state: TagState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (state: TagState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: TagState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addGamesToTag.fulfilled, (state, action) => {
      if (action.payload) {
        state.isListFetched = false;
      }
    });
    builder.addCase(getTagsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createTag.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createTag.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createTag.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateTag.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateTag.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image ?? item.image,
              icon:
                action.meta.arg.icon instanceof File
                  ? item.icon
                  : action.meta.arg.icon ?? item.icon,
              isActive: String(action.meta.arg.isActive),
            })
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateTag.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateTagsBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getTagItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
      state.initialPayload = action.payload
        ? {
            ...action.payload,
            translations: action.payload.translations?.map(
              (translation: Translation) => ({
                ...translation,
              }),
            ),
          }
        : undefined;
    });
    builder.addCase(deleteTagItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg;
    });

    builder.addCase(deleteTagItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });

    builder.addCase(deleteTagItem.rejected, (state) => {
      state.processingItemId = undefined;
    });

    builder.addCase(getTagItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
    });
    builder.addCase(getTagItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(getTagItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(updateTagItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
      state.isItemFetching = true;
    });
    builder.addCase(updateTagItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
      state.initialPayload = state.payload
        ? {
            ...state.payload,
            translations: state.payload.translations?.map((translation) => ({
              ...translation,
            })),
          }
        : undefined;
    });
    builder.addCase(updateTagItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  handleInvalidateList,
} = tagsSlice.actions;
